import {
  dispatch,
  ErrorVariants,
  FileUploadOutput,
  getWorkspaceId,
  showToast,
  useAppSelector,
  useLazyDeleteDocumentQuery,
} from '@zonofi/zono-money-store';
import { useState } from 'react';
import { MediaActiveModal } from '../types';
import { getDocumentAsync } from '../helper';
import { SVGS } from '../assets';

export enum MediaModalType {
  DISPATCH = 'dispatch',
  INVOICE = 'invoice',
  PAYMENT = 'payment',
}
interface UseMediaHandlersProps {
  setUploadedMedia: React.Dispatch<React.SetStateAction<FileUploadOutput[]>>;
  onAfterDelete?: (lastModified?: number, parseId?: number) => void;
  paymentId?: string;
  maxFiles?: number;
  type?: MediaModalType;
  isEditMode?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

export const useMediaHandlers = ({
  setUploadedMedia,
  onAfterDelete,
  paymentId = '',
  maxFiles = 5,
  type,
  isEditMode = false,
}: UseMediaHandlersProps) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [deleteFile] = useLazyDeleteDocumentQuery();
  const [selectedMedia, setSelectedMedia] = useState<FileUploadOutput>();
  const [activeModal, setActiveModal] = useState<MediaActiveModal>('NONE');

  const [imageParseState, setImageParseState] = useState<{
    shouldParseImage: boolean;
    imageIdToParse?: number;
  }>({
    shouldParseImage: false,
  });

  const onParsePress = () => {
    if (!selectedMedia) return;
    setImageParseState((p) => ({ ...p, shouldParseImage: true }));
    setActiveModal('NONE');
  };

  const onMediaPress = (media: FileUploadOutput) => {
    setSelectedMedia(media);
    setImageParseState((p) => ({ ...p, shouldParseImage: true }));
  };

  const onThreeDotPress = (media: FileUploadOutput) => {
    setSelectedMedia(media);
    setImageParseState((p) => ({ ...p, shouldParseImage: false }));
    setActiveModal('MEDIA_ACTIONS');
  };

  const handleFileSelect = async (type: 'gallery' | 'camera' | 'files') => {
    const result = await getDocumentAsync({
      type: type === 'files' ? 'application/*' : 'image/*',
      multiple: false,
    });

    if (result?.type === 'success' && result.output?.length) {
      const file = result.output[0];

      // File Type Validation
      if (!ALLOWED_TYPES.includes(file.file?.type)) {
        dispatch(
          showToast({
            message: 'Only JPG, PNG, and PDF files are allowed',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
        setActiveModal('NONE');
        return;
      }

      // File Size Validation
      if (file.size && file.size > MAX_FILE_SIZE) {
        dispatch(
          showToast({
            message: 'File size should be less than 5MB',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
        setActiveModal('NONE');
        return;
      }

      const newMedia: FileUploadOutput = {
        uri: file.uri,
        name: file.name,
        file: file.file as File,
        lastModified: file.lastModified,
        size: file.size,
        data: file.data,
      };

      setUploadedMedia((prev) => {
        if (maxFiles === 1) {
          return [newMedia];
        }

        if (prev.length >= maxFiles) {
          dispatch(
            showToast({
              message: `You can upload maximum ${maxFiles} file${
                maxFiles > 1 ? 's' : ''
              }`,
              type: ErrorVariants.DANGER,
              canDisplay: true,
              responseStatus: 'Error',
            })
          );
          return prev;
        }

        return [...prev, newMedia];
      });
    }
    setActiveModal('NONE');
  };

  const onDeletePress = async (media: FileUploadOutput) => {
    if (!selectedMedia) return;

    try {
      setUploadedMedia((prev) =>
        prev.filter((item) => item.lastModified !== selectedMedia.lastModified)
      );

      if ((media?.uri as string)?.startsWith('https://')) {
        const [sequenceNo] = media.name.split('/');
        await deleteFile({
          workspaceId: sellerWorkspaceId,
          documentType: 'payment',
          subType: 'transactions',
          entityId: paymentId,
          sequenceNo,
        });
      }

      onAfterDelete?.(
        selectedMedia.lastModified,
        imageParseState.imageIdToParse
      );

      setActiveModal('NONE');

      dispatch(
        showToast({
          message: 'Attachment deleted successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
    } catch {
      dispatch(
        showToast({
          message: 'Failed to Delete Attachment',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  };

  const mediaActions = [
    {
      icon: SVGS.ViewImage,
      text: 'View File',
      textClass: 'text-black',
      onPress: () => setActiveModal('IMAGE_PREVIEW'),
    },
    ...(isEditMode
      ? []
      : [
          {
            icon: SVGS.ParseIcon,
            text: 'Parse',
            textClass: 'text-black',
            onPress: onParsePress,
          },
          {
            icon: SVGS.DeleteIcon,
            text: 'Delete',
            textClass: 'text-red',
            onPress: onDeletePress,
          },
        ]),
  ];

  const uploadMediaActions = [
    {
      icon: SVGS.ImageFrame,
      text: 'Choose from Photos',
      textClass: 'text-black',
      onPress: () => handleFileSelect('gallery'),
    },
    {
      icon: SVGS.CameraIcon,
      text: 'Take Photo',
      textClass: 'text-black',
      onPress: () => handleFileSelect('camera'),
    },
    ...(type !== MediaModalType?.PAYMENT
      ? [
          {
            icon: SVGS.UploadFileBlack,
            text: 'Select from Files',
            textClass: 'text-black',
            onPress: () => handleFileSelect('files'),
          },
        ]
      : []),
  ];

  return {
    selectedMedia,
    activeModal,
    imageParseState,
    onMediaPress,
    onThreeDotPress,
    mediaActions,
    uploadMediaActions,
    setActiveModal,
    setImageParseState,
    setSelectedMedia,
  };
};
